<template>
  <div class="refunds-page pa-4 pa-md-6">
    <div class="page-container">
      <!-- Header -->
      <div class="d-flex flex-wrap align-center justify-space-between mb-4">
        <div class="d-flex align-center">
          <div class="header-icon-box me-3">
            <v-icon color="#082C69" size="24">mdi-cash-refund</v-icon>
          </div>
          <div>
            <h1 class="text-h6 font-weight-bold navy--text mb-0">طلبات الاسترداد</h1>
            <p class="text-caption text-muted mb-0">مراجعة ومعالجة جميع طلبات الاسترداد الخاصة بالعملاء</p>
          </div>
        </div>
        <div class="d-flex align-center gap-2 mt-2 mt-sm-0">
          <!-- Export CSV Button -->
          <v-btn
            color="#082C69"
            class="white--text text-none font-weight-bold px-4 rounded-lg csv-btn"
            elevation="1"
            height="36"
            :loading="exportLoading"
            @click="exportCsv"
          >
            <v-icon left size="16">mdi-file-delimited-outline</v-icon>
            تصدير CSV
          </v-btn>
        </div>
      </div>

      <!-- 6 KPI Cards in 1 Row (Responsive Grid) -->
      <div class="kpi-grid mb-4">
        <!-- 1. Submitted / New -->
        <v-card class="kpi-card elevation-1 rounded-xl pa-3 cursor-pointer" :class="{ 'kpi-active': filters.status === 'submitted' }" @click="filterByKpi('submitted')">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">طلبات جديدة</span>
            <div class="kpi-icon-circle bg-sky-light">
              <v-icon size="16" color="#0369A1">mdi-file-document-outline</v-icon>
            </div>
          </div>
          <div class="kpi-value text-sky mb-0">
            {{ summary.submitted_count }}
          </div>
          <div class="kpi-subtext text-muted">
            بانتظار بدء المراجعة
          </div>
        </v-card>

        <!-- 2. Customer Service Review -->
        <v-card class="kpi-card elevation-1 rounded-xl pa-3 cursor-pointer" :class="{ 'kpi-active': filters.status === 'customer_service_review' }" @click="filterByKpi('customer_service_review')">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">قيد مراجعة خدمة العملاء</span>
            <div class="kpi-icon-circle bg-amber-light">
              <v-icon size="16" color="#D97706">mdi-clock-outline</v-icon>
            </div>
          </div>
          <div class="kpi-value text-amber mb-0">
            {{ summary.customer_service_review_count }}
          </div>
          <div class="kpi-subtext text-muted">
            تحت فحص الفريق
          </div>
        </v-card>

        <!-- 3. Sent to Finance -->
        <v-card class="kpi-card elevation-1 rounded-xl pa-3 cursor-pointer" :class="{ 'kpi-active': filters.status === 'sent_to_finance' }" @click="filterByKpi('sent_to_finance')">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">مرسلة إلى المالية</span>
            <div class="kpi-icon-circle bg-purple-light">
              <v-icon size="16" color="#7C3AED">mdi-send-check</v-icon>
            </div>
          </div>
          <div class="kpi-value text-purple mb-0">
            {{ summary.sent_to_finance_count }}
          </div>
          <div class="kpi-subtext text-muted">
            بانتظار قرار المالية
          </div>
        </v-card>

        <!-- 4. Finance Approved -->
        <v-card class="kpi-card elevation-1 rounded-xl pa-3 cursor-pointer" :class="{ 'kpi-active': filters.status === 'finance_approved' }" @click="filterByKpi('finance_approved')">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">موافق عليها مالياً</span>
            <div class="kpi-icon-circle bg-emerald-light">
              <v-icon size="16" color="#059669">mdi-check-decagram</v-icon>
            </div>
          </div>
          <div class="kpi-value text-emerald mb-0">
            {{ summary.finance_approved_count }}
          </div>
          <div class="kpi-subtext text-muted">
            جاهزة للتنفيذ
          </div>
        </v-card>

        <!-- 5. Completed / Refunded -->
        <v-card class="kpi-card elevation-1 rounded-xl pa-3 cursor-pointer" :class="{ 'kpi-active': filters.status === 'completed' }" @click="filterByKpi('completed')">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">مكتملة / تم الاسترداد</span>
            <div class="kpi-icon-circle bg-teal-light">
              <v-icon size="16" color="#0D9488">mdi-cash-check</v-icon>
            </div>
          </div>
          <div class="kpi-value text-teal mb-0">
            {{ summary.completed_count }}
          </div>
          <div class="kpi-subtext text-muted">
            تم رد الأموال للعميل
          </div>
        </v-card>

        <!-- 6. Rejected -->
        <v-card class="kpi-card elevation-1 rounded-xl pa-3 cursor-pointer" :class="{ 'kpi-active': filters.status === 'rejected' }" @click="filterByKpi('rejected')">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-bold text-muted">مرفوضة</span>
            <div class="kpi-icon-circle bg-red-light">
              <v-icon size="16" color="#DC2626">mdi-close-circle-outline</v-icon>
            </div>
          </div>
          <div class="kpi-value text-red mb-0">
            {{ summary.rejected_count }}
          </div>
          <div class="kpi-subtext text-muted">
            خدمة العملاء أو المالية
          </div>
        </v-card>
      </div>

      <!-- Filters Bar Card -->
      <v-card class="filter-card elevation-1 rounded-xl mb-4">
        <!-- ROW 1: 4 Controls Equal Width (100% width) -->
        <div class="filter-row-1">
          <v-select
            v-model="filters.period"
            :items="periodOptions"
            label="الفترة"
            outlined
            dense
            hide-details
            class="rounded-lg custom-field"
            @change="onPeriodChange"
          ></v-select>

          <v-select
            v-model="filters.status"
            :items="statusOptions"
            label="الحالة"
            outlined
            dense
            hide-details
            class="rounded-lg custom-field"
          ></v-select>

          <v-select
            v-model="filters.refund_type"
            :items="refundTypeOptions"
            label="نوع الاسترداد"
            outlined
            dense
            hide-details
            class="rounded-lg custom-field"
          ></v-select>

          <v-select
            v-model="filters.payment_method"
            :items="paymentMethodOptions"
            label="طريقة رد المبلغ"
            outlined
            dense
            hide-details
            class="rounded-lg custom-field"
          ></v-select>
        </div>

        <!-- Custom Date Range Sub-Row (if period === 'custom') -->
        <div v-if="filters.period === 'custom'" class="filter-custom-dates mt-3">
          <v-text-field
            v-model="filters.date_from"
            type="date"
            label="من تاريخ"
            outlined
            dense
            hide-details
            class="rounded-lg custom-field"
          ></v-text-field>
          <v-text-field
            v-model="filters.date_to"
            type="date"
            label="إلى تاريخ"
            outlined
            dense
            hide-details
            class="rounded-lg custom-field"
          ></v-text-field>
        </div>

        <!-- ROW 2: Balanced Full Width (1fr 1fr 2fr auto) -->
        <div class="filter-row-2 mt-3">
          <v-text-field
            v-model="filters.request_code"
            label="رقم الطلب"
            placeholder="REF-000001"
            outlined
            dense
            hide-details
            class="rounded-lg custom-field"
            clearable
            @keyup.enter="applyFilters"
          ></v-text-field>

          <v-text-field
            v-model="filters.booking_id"
            label="رقم الحجز"
            placeholder="BKG-..."
            outlined
            dense
            hide-details
            class="rounded-lg custom-field"
            clearable
            @keyup.enter="applyFilters"
            @click:clear="filters.booking_id = ''; $nextTick(() => applyFilters())"
          ></v-text-field>

          <v-text-field
            v-model="filters.search"
            prepend-inner-icon="mdi-magnify"
            label="بحث بالعميل / الهاتف"
            placeholder="ابحث باسم العميل أو رقم الهاتف..."
            outlined
            dense
            hide-details
            class="rounded-lg custom-field filter-search-input"
            clearable
            @keyup.enter="applyFilters"
            @click:clear="filters.search = ''; $nextTick(() => applyFilters())"
          ></v-text-field>

          <div class="filter-btn-group">
            <v-btn
              color="#082C69"
              class="white--text font-weight-bold rounded-lg filter-btn apply-btn"
              elevation="0"
              @click="applyFilters"
            >
              <v-icon left size="16">mdi-filter</v-icon>
              تطبيق
            </v-btn>
            <v-btn
              outlined
              color="#64748B"
              class="font-weight-bold rounded-lg filter-btn reset-btn"
              @click="resetFilters"
            >
              <v-icon left size="16">mdi-refresh</v-icon>
              إعادة ضبط
            </v-btn>
          </div>
        </div>
      </v-card>

      <!-- Refunds Table Card -->
      <v-card class="elevation-1 rounded-xl overflow-hidden">
        <v-data-table
          :headers="headers"
          :items="refunds"
          :loading="loading"
          :items-per-page="perPage"
          hide-default-footer
          class="refunds-table"
          no-data-text="لا توجد طلبات استرداد مطابقة للبحث"
          loading-text="جاري تحميل طلبات الاسترداد..."
        >
          <!-- Row Index -->
          <template v-slot:[`item.index`]="{ index }">
            <span class="text-caption font-weight-bold text-muted">
              {{ (currentPage - 1) * perPage + index + 1 }}
            </span>
          </template>

          <!-- Request Code -->
          <template v-slot:[`item.request_code`]="{ item }">
            <router-link :to="`/customer-service/refunds/${item.id}`" class="text-decoration-none">
              <span class="mono-code navy--text font-weight-bold">{{ item.request_code }}</span>
            </router-link>
          </template>

          <!-- Booking Ref -->
          <template v-slot:[`item.booking_ref`]="{ item }">
            <span class="mono-code font-weight-medium">{{ item.booking_ref }}</span>
          </template>

          <!-- Customer -->
          <template v-slot:[`item.customer`]="{ item }">
            <div class="d-flex flex-column py-1">
              <span class="font-weight-bold navy--text text-body-2">{{ item.customer_name }}</span>
              <span class="text-caption text-muted dir-ltr text-right">{{ item.customer_phone }}</span>
            </div>
          </template>

          <!-- Trip Ref -->
          <template v-slot:[`item.trip_ref`]="{ item }">
            <span class="mono-code">{{ item.trip_ref }}</span>
          </template>

          <!-- Route -->
          <template v-slot:[`item.route_name`]="{ item }">
            <span class="text-body-2 font-weight-medium">{{ item.route_name }}</span>
          </template>

          <!-- Refund Type -->
          <template v-slot:[`item.refund_type_label`]="{ item }">
            <v-chip
              x-small
              outlined
              :color="!item.refund_type ? '#64748B' : (item.refund_type === 'partial' ? '#B45309' : '#0369A1')"
              class="font-weight-bold"
            >
              {{ item.refund_type_label || 'لم يُحدد بعد' }}
            </v-chip>
          </template>

          <!-- Requested Amount -->
          <template v-slot:[`item.requested_amount`]="{ item }">
            <span class="font-weight-bold navy--text text-body-2">
              {{ item.requested_amount }} <small class="text-muted">ج.م</small>
            </span>
          </template>

          <!-- Approved Amount -->
          <template v-slot:[`item.approved_amount`]="{ item }">
            <span v-if="item.approved_amount !== null" class="font-weight-bold text-emerald text-body-2">
              {{ item.approved_amount }} <small class="text-muted">ج.م</small>
            </span>
            <span v-else class="text-muted font-weight-medium">قيد المراجعة</span>
          </template>

          <!-- Payment Method -->
          <template v-slot:[`item.payment_method_label`]="{ item }">
            <span class="text-caption font-weight-medium">{{ item.payment_method_label }}</span>
          </template>

          <!-- Status Badge -->
          <template v-slot:[`item.status_label`]="{ item }">
            <span
              class="status-badge"
              :style="{ backgroundColor: item.status_color.bg, color: item.status_color.text }"
            >
              {{ item.status_label }}
            </span>
          </template>

          <!-- Submitted Date -->
          <template v-slot:[`item.submitted_at`]="{ item }">
            <span class="text-caption text-muted">{{ item.submitted_at || '—' }}</span>
          </template>

          <!-- Actions -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center justify-center">
              <v-btn
                small
                outlined
                color="#082C69"
                class="rounded-lg font-weight-bold px-3 action-btn"
                :to="`/customer-service/refunds/${item.id}`"
              >
                <v-icon left size="14">mdi-eye-outline</v-icon>
                عرض / مراجعة
              </v-btn>
            </div>
          </template>
        </v-data-table>

        <!-- Pagination & Footer -->
        <div class="d-flex flex-wrap align-center justify-space-between pa-4 border-top">
          <div class="text-caption text-muted mb-2 mb-sm-0">
            عرض {{ (currentPage - 1) * perPage + 1 }} إلى {{ Math.min(currentPage * perPage, totalItems) }} من إجمالي {{ totalItems }} طلب
          </div>
          <v-pagination
            v-if="lastPage > 1"
            v-model="currentPage"
            :length="lastPage"
            :total-visible="7"
            color="#082C69"
            @input="fetchRefunds"
          ></v-pagination>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  name: 'CustomerServiceRefundsIndex',
  data() {
    return {
      loading: false,
      exportLoading: false,
      refunds: [],
      currentPage: 1,
      lastPage: 1,
      perPage: 15,
      totalItems: 0,
      summary: {
        submitted_count: 0,
        customer_service_review_count: 0,
        sent_to_finance_count: 0,
        finance_approved_count: 0,
        completed_count: 0,
        rejected_count: 0,
        total_count: 0,
      },
      filters: {
        period: 'all',
        date_from: '',
        date_to: '',
        status: '',
        refund_type: '',
        payment_method: '',
        request_code: '',
        booking_id: '',
        search: '',
      },
      periodOptions: [
        { text: 'كل الفترات', value: 'all' },
        { text: 'اليوم', value: 'today' },
        { text: 'هذا الأسبوع', value: 'this_week' },
        { text: 'هذا الشهر', value: 'this_month' },
        { text: 'تاريخ مخصص', value: 'custom' },
      ],
      statusOptions: [
        { text: 'جميع الحالات', value: '' },
        { text: 'طلب جديد', value: 'submitted' },
        { text: 'قيد مراجعة خدمة العملاء', value: 'customer_service_review' },
        { text: 'مرسل إلى المالية', value: 'sent_to_finance' },
        { text: 'موافق عليه مالياً', value: 'finance_approved' },
        { text: 'مكتمل / تم الاسترداد', value: 'completed' },
        { text: 'مرفوض', value: 'rejected' },
      ],
      refundTypeOptions: [
        { text: 'جميع الأنواع', value: '' },
        { text: 'استرداد كلي', value: 'full' },
        { text: 'استرداد جزئي', value: 'partial' },
      ],
      paymentMethodOptions: [
        { text: 'جميع طرق الدفع', value: '' },
        { text: 'المحفظة / إلكتروني', value: '0' },
        { text: 'نقداً', value: '1' },
        { text: 'باقة اشتراك (Route Pass)', value: '2' },
      ],
      headers: [
        { text: '#', value: 'index', sortable: false, width: '45px' },
        { text: 'رقم الطلب', value: 'request_code', sortable: false },
        { text: 'رقم الحجز', value: 'booking_ref', sortable: false },
        { text: 'العميل', value: 'customer', sortable: false },
        { text: 'رقم الرحلة', value: 'trip_ref', sortable: false },
        { text: 'المسار', value: 'route_name', sortable: false },
        { text: 'نوع الاسترداد', value: 'refund_type_label', sortable: false },
        { text: 'المبلغ المطلوب', value: 'requested_amount', sortable: false },
        { text: 'المبلغ المعتمد', value: 'approved_amount', sortable: false },
        { text: 'طريقة رد المبلغ', value: 'payment_method_label', sortable: false },
        { text: 'الحالة', value: 'status_label', sortable: false },
        { text: 'التاريخ', value: 'submitted_at', sortable: false },
        { text: 'الإجراءات', value: 'actions', sortable: false, align: 'center' },
      ],
    };
  },
  created() {
    this.fetchRefunds();
    this.checkDeepLink();
  },
  watch: {
    '$route.query'() {
      this.checkDeepLink();
    }
  },
  methods: {
    checkDeepLink() {
      const q = this.$route.query;
      if (!q) return;
      if (q.id) {
        this.$router.push(`/customer-service/refunds/${q.id}`).catch(() => {});
      } else if (q.request_code) {
        this.filters.search = q.request_code;
        this.applyFilters();
      }
    },
    async fetchRefunds() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          ...this.buildFilterParams(),
        };

        const response = await axios.get('/customer-service/refunds', { params });
        const resData = response.data;

        this.refunds = resData.data || [];
        this.currentPage = resData.current_page || 1;
        this.lastPage = resData.last_page || 1;
        this.totalItems = resData.total || 0;
        if (resData.summary) {
          this.summary = resData.summary;
        }
      } catch (error) {
        console.error('Error fetching refunds:', error);
      } finally {
        this.loading = false;
      }
    },
    buildFilterParams() {
      const p = {};
      if (this.filters.status) p.status = this.filters.status;
      if (this.filters.refund_type) p.refund_type = this.filters.refund_type;
      if (this.filters.payment_method !== '') p.payment_method = this.filters.payment_method;
      if (this.filters.request_code) p.request_code = this.filters.request_code;
      if (this.filters.booking_id) p.booking_id = this.filters.booking_id;
      if (this.filters.search) p.search = this.filters.search;
      if (this.filters.date_from) p.date_from = this.filters.date_from;
      if (this.filters.date_to) p.date_to = this.filters.date_to;
      return p;
    },
    onPeriodChange(val) {
      const today = new Date();
      const format = (d) => d.toISOString().split('T')[0];

      if (val === 'today') {
        const dStr = format(today);
        this.filters.date_from = dStr;
        this.filters.date_to = dStr;
      } else if (val === 'this_week') {
        const firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
        const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 6));
        this.filters.date_from = format(firstDay);
        this.filters.date_to = format(lastDay);
      } else if (val === 'this_month') {
        const first = new Date(today.getFullYear(), today.getMonth(), 1);
        const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        this.filters.date_from = format(first);
        this.filters.date_to = format(last);
      } else if (val === 'all') {
        this.filters.date_from = '';
        this.filters.date_to = '';
      }
      this.applyFilters();
    },
    filterByKpi(statusKey) {
      if (this.filters.status === statusKey) {
        this.filters.status = '';
      } else {
        this.filters.status = statusKey;
      }
      this.applyFilters();
    },
    applyFilters() {
      this.currentPage = 1;
      this.fetchRefunds();
    },
    resetFilters() {
      this.filters = {
        period: 'all',
        date_from: '',
        date_to: '',
        status: '',
        refund_type: '',
        payment_method: '',
        request_code: '',
        booking_id: '',
        search: '',
      };
      this.currentPage = 1;
      this.fetchRefunds();
    },
    async exportCsv() {
      this.exportLoading = true;
      try {
        const params = this.buildFilterParams();
        const response = await axios.get('/customer-service/refunds/export-csv', {
          params,
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `beepgo-refunds-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err) {
        console.error('CSV Export Error:', err);
      } finally {
        this.exportLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.refunds-page {
  background-color: #F8FAFC;
  min-height: 100vh;
  direction: rtl;
}

.page-container {
  max-width: 1600px;
  margin: 0 auto;
}

.navy--text {
  color: #082C69 !important;
}

.text-muted {
  color: #64748B !important;
}

.text-sky {
  color: #0369A1 !important;
}

.text-amber {
  color: #D97706 !important;
}

.text-purple {
  color: #7C3AED !important;
}

.text-emerald {
  color: #059669 !important;
}

.text-teal {
  color: #0D9488 !important;
}

.text-red {
  color: #DC2626 !important;
}

.bg-sky-light {
  background-color: #E0F2FE;
}

.bg-amber-light {
  background-color: #FEF3C7;
}

.bg-purple-light {
  background-color: #EDE9FE;
}

.bg-emerald-light {
  background-color: #DCFCE7;
}

.bg-teal-light {
  background-color: #CCFBF1;
}

.bg-red-light {
  background-color: #FEE2E2;
}

.header-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background-color: #E0E7FF;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 6 KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
}

@media (max-width: 1400px) {
  .kpi-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  border: 1px solid #E2E8F0;
  background: #FFFFFF;
  transition: all 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(8, 44, 105, 0.08) !important;
}

.kpi-active {
  border: 2px solid #082C69 !important;
  background-color: #F8FAFC !important;
}

.cursor-pointer {
  cursor: pointer;
}

.kpi-icon-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-value {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.2;
}

.kpi-subtext {
  font-size: 0.75rem;
  margin-top: 2px;
}

.filter-card {
  border: 1px solid #E2E8F0;
  background: #FFFFFF;
  padding: 16px 18px !important;
}

.filter-row-1 {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
}

.filter-custom-dates {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
}

.filter-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr 2fr auto;
  gap: 12px;
  align-items: center;
  width: 100%;
}

.filter-btn-group {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.filter-btn {
  height: 42px !important;
  min-height: 42px !important;
  font-size: 0.875rem !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px !important;
}

.apply-btn {
  background-color: #082C69 !important;
}

.reset-btn {
  border-color: #CBD5E1 !important;
  color: #475569 !important;
  background-color: transparent !important;
}

.custom-field ::v-deep .v-input__control {
  font-size: 0.875rem;
}

.custom-field ::v-deep .v-input__slot {
  min-height: 42px !important;
  height: 42px !important;
  margin-bottom: 0 !important;
}

.custom-field ::v-deep .v-select__slot,
.custom-field ::v-deep .v-text-field__slot {
  min-height: 42px !important;
  height: 42px !important;
  align-items: center !important;
}

.custom-field ::v-deep .v-input__append-inner {
  margin-top: 9px !important;
}

.custom-field ::v-deep .v-input__prepend-inner {
  margin-top: 9px !important;
}

.custom-field ::v-deep .v-label {
  top: 11px !important;
}

.custom-field ::v-deep .v-label--active {
  transform: translateY(-18px) scale(0.75) !important;
}

@media (max-width: 1080px) {
  .filter-row-1 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .filter-row-2 {
    grid-template-columns: 1fr 1fr;
  }
  .filter-search-input {
    grid-column: span 2;
  }
  .filter-btn-group {
    grid-column: span 2;
    justify-content: flex-end;
  }
}

@media (max-width: 640px) {
  .filter-row-1 {
    grid-template-columns: 1fr;
  }
  .filter-row-2 {
    grid-template-columns: 1fr;
  }
  .filter-search-input {
    grid-column: span 1;
  }
  .filter-btn-group {
    grid-column: span 1;
    width: 100%;
    justify-content: stretch;
  }
  .filter-btn {
    flex: 1;
  }
}

.mono-code {
  font-family: monospace, sans-serif;
  letter-spacing: 0.5px;
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
}

.action-btn {
  font-size: 0.75rem !important;
  text-transform: none !important;
}

.border-top {
  border-top: 1px solid #E2E8F0;
}

.gap-2 {
  gap: 8px;
}

.dir-ltr {
  direction: ltr;
}
</style>
